import moment from 'moment'
import { prop, flatten, defaultTo, values, length, pipe } from 'ramda'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useResponsiveValue } from '@machobear/design-system'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  StyledTableCell,
  TableHeaderText
} from '@/components/showings-table'
import { EmptyListComponent } from '@/components/table-list'
import ShowingsContainer from '@/features/Listing/showings-container'
import useMediaQuery from '@/hooks/use-media-query'
import { select } from '@/store'
import { breakpoints, colors, structure, fontSizes, fontWeights } from '@/styles'
import ListingShowingsHeader from './listing-showings-header'
import backgroundBottom from './images/background-bottom.png'
import backgroundBottomMobile from './images/background-bottom-mobile.png'
import { showAddSlotModal } from './add-a-slot-modal'

const Wrapper = styled.div`
  background-color: ${colors.white};
  padding: 0 0 220px;
  flex: 1;
  display: flex;
  flex-direction: column;

  background-image: url(${backgroundBottom});
  background-repeat: no-repeat;
  background-position: 100% 100%;
  background-size: 270px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - 115px);
    padding-bottom: 125px;
    margin-bottom: 56px;

    background-image: url(${backgroundBottomMobile});
    background-repeat: no-repeat;
    background-position: 100% 99%;
    background-size: 140px;
  }
`

const TableOutterWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const TableWrapper = styled.div`
  flex: 1;
  max-width: 1130px;
  margin: 0 50px;
  height: fit-content;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0;
  }
`

const MobileButtonWrapper = styled.div`
  display: flex;
  height: 56px;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.white};
  box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.3);
`

const BackButton = styled.button`
  margin: 0;
  padding: 10px;
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.39px;
  line-height: 20px;
  text-align: center;
  color: ${colors.secondary};
  background: ${colors.white};
  border: 2px solid ${colors.secondary};
  cursor: pointer;
  flex: 1;
`

const SubmitButton = styled(BackButton)`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.39px;
  line-height: 20px;
  text-align: center;
  color: ${colors.white};
  background: ${colors.secondary};
  border: none;
`

const ListingShowings = ({
  match: {
    params: { listingId }
  },
  history
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [currentTab, setCurrentTab] = useState('upcoming')
  const [tabFilteredData, setTabFilteredData] = useState([])
  const [expanded, setExpanded] = useState(true)

  const displayMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)

  const [tabCount, setTabCount] = useState({
    upcoming: 0,
    history: 0
  })

  useEffect(() => {
    dispatch.listing.getAppointments(listingId)
  }, [dispatch.listing, listingId])

  const showings = useSelector(state => select.listing.appointments(state, { listingId }))

  useEffect(() => {
    setTabFilteredData(showings[currentTab] || [])
    setTabCount({
      upcoming: pipe(prop('upcoming'), defaultTo([]), flatten, length)(showings),
      history: pipe(prop('history'), defaultTo([]), flatten, length)(showings)
    })
  }, [currentTab, showings])

  const handleSearch = searchString => {
    if (!searchString) setTabFilteredData(showings[currentTab])

    const filtered = showings[currentTab].reduce((accum, item) => {
      const filterdItem = item.filter(appointment => {
        return moment(appointment.meeting_at)
          .format('ddd dddd MMM MMMM')
          .toLowerCase()
          .includes(searchString.trim().toLowerCase())
          ? JSON.stringify(values(appointment)).toLowerCase()
          : JSON.stringify(values(appointment))
              .toLowerCase()
              .includes(searchString.trim().toLowerCase())
      })

      if (filterdItem.length > 0) {
        return [...accum, filterdItem]
      }
      return accum
    }, [])

    setTabFilteredData(filtered)
  }

  const MobileButtons = () => (
    <MobileButtonWrapper>
      <BackButton onClick={history.goBack} type="button">
        {t('common.back', 'Back')}
      </BackButton>
      <SubmitButton
        onClick={() =>
          showAddSlotModal({
            listingId
          })
        }
        type="button"
      >
        {t('l.listing.showings.add_slot.button', 'Add Slot')}
      </SubmitButton>
    </MobileButtonWrapper>
  )

  return (
    <Wrapper>
      <Helmet>
        <title>{t('l.listing.view.showings.title', 'Showings')}</title>
      </Helmet>

      <ListingShowingsHeader
        tabCount={tabCount}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        onSearch={handleSearch}
        listingId={listingId}
      />

      <TableOutterWrapper>
        <TableWrapper>
          <Table>
            {!displayTablet && (
              <TableHeader>
                <TableRow sticky top={structure.header.height + 198}>
                  <TableHeaderCell width={120}>
                    <TableHeaderText firstCell>
                      {t('l.listing.view.date_and_time.text', 'Date & Time')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <TableHeaderText>{t('common.duration', 'Duration')}</TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <TableHeaderText>{t('common.attendees', 'Attendees')}</TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <TableHeaderText>&nbsp;</TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <TableHeaderText lastCell onClick={() => setExpanded(!expanded)}>
                      {expanded
                        ? t('l.listing.list.collapse_all.text', 'Collapse All')
                        : t('l.listing.list.expand_all.text', 'Expand All')}
                    </TableHeaderText>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
            )}
            <TableBody>
              {tabFilteredData.length > 0 ? (
                tabFilteredData.map((showingsDate, index) => (
                  <ShowingsContainer
                    currentTab={currentTab}
                    key={index}
                    showingsDate={showingsDate}
                    listingId={listingId}
                    expanded={expanded}
                  />
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={5}>
                    <EmptyListComponent />
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </TableOutterWrapper>
      {displayMobile && <MobileButtons />}
    </Wrapper>
  )
}

export default ListingShowings
